import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, X, IndianRupee, LogIn, User as UserIcon, LayoutDashboard } from "lucide-react";
import { NAV_LINKS, RATES as FALLBACK_RATES } from "../mock";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [rates, setRates] = useState(FALLBACK_RATES);
  const { user, login, logout } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    api.get("/rates").then((r) => setRates(r.data)).catch(() => {});
  }, []);

  const go = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const AccountBtn = () => {
    if (user) {
      return (
        <Link to={user.role === "admin" ? "/admin" : "/account"}
          className="hidden sm:inline-flex items-center gap-2 border border-gold text-gold px-4 py-2.5 font-ui text-[11px] tracking-[0.2em] uppercase hover:bg-gold hover:text-ink transition-colors">
          {user.role === "admin" ? <LayoutDashboard size={14} /> : <UserIcon size={14} />}
          {user.name?.split(" ")[0] || "Account"}
        </Link>
      );
    }
    return (
      <button onClick={() => login("/account")}
        className="hidden sm:inline-flex items-center gap-2 border border-gold text-gold px-4 py-2.5 font-ui text-[11px] tracking-[0.2em] uppercase hover:bg-gold hover:text-ink transition-colors">
        <LogIn size={14} /> Sign In
      </button>
    );
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      <div className="hidden md:flex items-center justify-between px-8 py-1.5 text-[11px] font-ui tracking-widest text-muted-foreground border-b border-white/5 bg-ink/90 backdrop-blur">
        <span className="flex items-center gap-2">
          <span className="h-1.5 w-1.5 rounded-full bg-gold animate-pulse" />
          TODAY'S RATES · {rates.city?.toUpperCase()}
        </span>
        <span className="flex items-center gap-6">
          <span className="text-muted-foreground">GOLD 24K <span className="text-gold">₹{rates.gold24k.value.toLocaleString('en-IN')}</span> /10g</span>
          <span className="text-muted-foreground">SILVER <span className="text-gold">₹{rates.silver.value.toLocaleString('en-IN')}</span> /kg</span>
        </span>
        <span>Updated {rates.updated}</span>
      </div>

      <nav className={`transition-all duration-300 ${scrolled ? "bg-ink/95 backdrop-blur shadow-lg shadow-black/40" : "bg-transparent"}`}>
        <div className="max-w-[1400px] mx-auto px-6 md:px-8 flex items-center justify-between h-[70px]">
          <a href="#top" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="flex flex-col leading-none">
            <span className="font-display text-2xl smallcaps tracking-[0.35em] text-white">TwinBrain</span>
            <span className="font-ui text-[9px] tracking-[0.4em] text-gold mt-0.5">JEWELLERY · NAGPUR</span>
          </a>

          <div className="hidden lg:flex items-center gap-6">
            {NAV_LINKS.map((l) => (
              <button
                key={l.href}
                onClick={() => go(l.href)}
                className={`font-ui text-[11px] tracking-[0.18em] uppercase transition-colors flex items-center gap-1.5 ${l.highlight ? "text-gold" : "text-gray-300 hover:text-gold"}`}
              >
                {l.highlight && <span className="h-1.5 w-1.5 rounded-full bg-gold" />}
                {l.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <AccountBtn />
            {user && <button onClick={logout} className="hidden sm:inline-flex font-ui text-[10px] tracking-[0.15em] uppercase text-gray-400 hover:text-gold transition-colors">Logout</button>}
            <button className="lg:hidden text-white" onClick={() => setOpen(!open)}>
              {open ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden bg-ink/98 backdrop-blur border-t border-white/5 px-6 py-4 flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <button
                key={l.href}
                onClick={() => go(l.href)}
                className={`text-left font-ui text-sm tracking-[0.15em] uppercase py-2.5 border-b border-white/5 ${l.highlight ? "text-gold" : "text-gray-300"}`}
              >
                {l.label}
              </button>
            ))}
            {user ? (
              <div className="flex gap-3 mt-3">
                <Link to={user.role === "admin" ? "/admin" : "/account"} className="flex-1 text-center border border-gold text-gold font-ui text-[11px] tracking-[0.2em] uppercase py-3">{user.role === "admin" ? "Admin" : "My Account"}</Link>
                <button onClick={logout} className="flex-1 border border-white/15 text-gray-300 font-ui text-[11px] tracking-[0.2em] uppercase py-3">Logout</button>
              </div>
            ) : (
              <button onClick={() => login("/account")} className="mt-3 gold-btn font-ui text-[11px] tracking-[0.2em] uppercase py-3">Sign In with Google</button>
            )}
            <div className="flex items-center gap-4 mt-3 text-[11px] font-ui tracking-widest text-muted-foreground">
              <span className="flex items-center gap-1 text-gold"><IndianRupee size={12} />{rates.gold24k.value.toLocaleString('en-IN')}/10g</span>
              <span className="flex items-center gap-1 text-gold"><IndianRupee size={12} />{rates.silver.value.toLocaleString('en-IN')}/kg</span>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Navbar;
